import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { SiteHeader } from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Database, Plus, MagnifyingGlass, FloppyDisk, Trash, PencilSimple, DownloadSimple,
  Upload, ListBullets, Tag, ShieldWarning, X,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: `${API_URL}/api/admin/formula-db`, withCredentials: true });

const NUMERIC_FIELDS = new Set([
  'molecular_weight', 'noael', 'dermal_absorption',
  'uncertainty_factor', 'eu_annex_iii_limit',
]);
const BOOL_FIELDS = new Set([
  'eu_annex_ii', 'eu_annex_iii', 'ifra_restricted', 'prop_65',
  'genotoxicity', 'mutagenicity', 'carcinogenic',
  'reproductive_toxic', 'phototoxic',
]);
const FLAG_OPTIONS = [
  ['eu_annex_ii', 'EU Annex II (banned)'],
  ['eu_annex_iii', 'EU Annex III (restricted)'],
  ['ifra_restricted', 'IFRA restricted'],
  ['prop_65', 'Prop 65'],
  ['genotoxicity', 'Genotoxic'],
  ['mutagenicity', 'Mutagenic'],
  ['carcinogenic', 'Carcinogenic'],
  ['reproductive_toxic', 'Reprotoxic'],
  ['phototoxic', 'Phototoxic'],
];

function StatCard({ label, value, color = 'text-[#111827]' }) {
  return (
    <div className="card-flat p-4">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">{label}</p>
      <p className={`text-2xl font-black tabular-nums ${color}`}>{value}</p>
    </div>
  );
}

function FlagPill({ label, on }) {
  return (
    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
      on
        ? 'bg-[#FDE8E8] text-[#9B1C1C] border-[#F8B4B4]'
        : 'bg-gray-50 text-gray-400 border-gray-200'
    }`}>{label}</span>
  );
}

function EmptyVal({ v }) {
  if (v === undefined || v === null || v === '') return <span className="text-gray-300">—</span>;
  if (typeof v === 'number') return <span className="tabular-nums">{v}</span>;
  return <>{String(v)}</>;
}

// --------- Editor dialog ----------
function IngredientEditor({ open, onClose, initial, onSaved, mode }) {
  // mode: 'create' | 'edit'
  const [form, setForm] = useState({});
  const [rationale, setRationale] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(initial || { inci_name: '' });
    setRationale('');
  }, [initial, open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setNum = (k, v) => setForm(f => ({ ...f, [k]: v === '' ? null : parseFloat(v) }));

  const submit = async () => {
    setSaving(true);
    try {
      if (mode === 'create') {
        const payload = { ...form };
        if (!payload.inci_name?.trim()) {
          toast.error('INCI name is required');
          setSaving(false); return;
        }
        await ax.post('/ingredients', payload);
        toast.success(`Created "${payload.inci_name}"`);
      } else {
        if (!rationale.trim() || rationale.trim().length < 4) {
          toast.error('Rationale (min 4 chars) required for edits');
          setSaving(false); return;
        }
        const { inci_name, created_at, updated_at, created_by, updated_by,
                 audit, ...fields } = form;
        await ax.patch(`/ingredients/${encodeURIComponent(initial.inci_name)}`, {
          rationale, fields,
        });
        toast.success(`Saved changes to "${initial.inci_name}"`);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ingredient-editor">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add ingredient' : `Edit · ${initial?.inci_name}`}</DialogTitle>
          <DialogDescription>
            Admin-only edits to the Aᴳ master ingredient DB. All changes are audit-logged.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <Field label="INCI Name *">
            <Input value={form.inci_name || ''} onChange={e => set('inci_name', e.target.value)}
              disabled={mode === 'edit'} data-testid="form-inci-name" />
          </Field>
          <Field label="CAS Number">
            <Input value={form.cas_number || ''} onChange={e => set('cas_number', e.target.value)} />
          </Field>
          <Field label="EINECS Number">
            <Input value={form.einecs_number || ''} onChange={e => set('einecs_number', e.target.value)} />
          </Field>
          <Field label="Function class">
            <Input value={form.function_class || ''} onChange={e => set('function_class', e.target.value)} />
          </Field>
          <Field label="Molecular weight">
            <Input type="number" step="any" value={form.molecular_weight ?? ''} onChange={e => setNum('molecular_weight', e.target.value)} />
          </Field>
          <Field label="NOAEL (mg/kg bw/day)">
            <Input type="number" step="any" value={form.noael ?? ''} onChange={e => setNum('noael', e.target.value)} data-testid="form-noael" />
          </Field>
          <Field label="Dermal absorption (0–1)">
            <Input type="number" step="0.01" min="0" max="1" value={form.dermal_absorption ?? ''} onChange={e => setNum('dermal_absorption', e.target.value)} />
          </Field>
          <Field label="Uncertainty factor">
            <Input type="number" step="any" value={form.uncertainty_factor ?? ''} onChange={e => setNum('uncertainty_factor', e.target.value)} />
          </Field>
          <Field label="Skin sensitisation">
            <Input value={form.skin_sensitization || ''} placeholder="e.g. Not classified / Strong / Moderate" onChange={e => set('skin_sensitization', e.target.value)} />
          </Field>
          <Field label="EU Annex III limit (%)">
            <Input type="number" step="any" value={form.eu_annex_iii_limit ?? ''} onChange={e => setNum('eu_annex_iii_limit', e.target.value)} />
          </Field>

          <div className="col-span-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-2">Hazard / Regulatory flags</p>
            <div className="flex flex-wrap gap-2">
              {FLAG_OPTIONS.map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-xs px-2.5 py-1.5 border rounded cursor-pointer hover:bg-gray-50">
                  <input type="checkbox" checked={!!form[k]} onChange={e => set(k, e.target.checked)} data-testid={`form-flag-${k}`} />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <Field label="NOAEL rationale" wide>
            <Textarea value={form.noael_rationale || ''} rows={2} onChange={e => set('noael_rationale', e.target.value)} />
          </Field>
          <Field label="Conclusions" wide>
            <Textarea value={form.conclusions || ''} rows={2} onChange={e => set('conclusions', e.target.value)} />
          </Field>
          <Field label="Notes" wide>
            <Textarea value={form.notes || ''} rows={2} onChange={e => set('notes', e.target.value)} />
          </Field>
          <Field label="Source / citation" wide>
            <Input value={form.source || ''} onChange={e => set('source', e.target.value)} />
          </Field>

          {mode === 'edit' && (
            <Field label="Rationale for this edit (audit-logged) *" wide>
              <Textarea value={rationale} rows={2} onChange={e => setRationale(e.target.value)}
                placeholder="e.g. SCCS Opinion XYZ updated NOAEL to 50 mg/kg bw/day…"
                data-testid="form-edit-rationale" />
            </Field>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="editor-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0B1426] hover:bg-[#1B2538] text-white" data-testid="editor-save">
            <FloppyDisk size={14} className="mr-1" weight="fill" />
            {saving ? 'Saving…' : (mode === 'create' ? 'Create' : 'Save changes')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, wide, children }) {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">{label}</p>
      {children}
    </div>
  );
}

// --------- Delete confirm ----------
function DeleteConfirm({ open, ingredient, onClose, onDone }) {
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (rationale.trim().length < 4) { toast.error('Rationale required (min 4 chars)'); return; }
    setBusy(true);
    try {
      await ax.delete(`/ingredients/${encodeURIComponent(ingredient.inci_name)}`, {
        params: { rationale },
      });
      toast.success(`Deleted "${ingredient.inci_name}"`);
      onDone?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#9B1C1C]">Delete ingredient</DialogTitle>
          <DialogDescription>
            This will permanently remove <strong>{ingredient?.inci_name}</strong> from the Aᴳ DB.
            Audit-logged.
          </DialogDescription>
        </DialogHeader>
        <Textarea rows={3} value={rationale} onChange={e => setRationale(e.target.value)}
          placeholder="Rationale for deletion (min 4 chars)" data-testid="delete-rationale" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={busy} data-testid="delete-confirm">
            <Trash size={14} className="mr-1" weight="fill" /> Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --------- Bulk import dialog ----------
function BulkImportDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [rationale, setRationale] = useState('');
  const [mode, setMode] = useState('upsert');
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const submit = async () => {
    if (!file) { toast.error('Pick a CSV file'); return; }
    if (rationale.trim().length < 4) { toast.error('Rationale required'); return; }
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true); setReport(null);
    try {
      const r = await ax.post('/bulk-import-csv', fd, {
        params: { rationale, mode },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReport(r.data);
      toast.success(`Imported: ${r.data.created} created, ${r.data.updated} updated`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import (CSV)</DialogTitle>
          <DialogDescription>
            CSV columns: <code className="text-[10px] bg-gray-100 px-1 py-0.5 rounded">inci_name, cas_number, function_class, noael, …</code>{' '}
            (any subset of admin-DB fields). Headers required. Numeric fields auto-coerced, boolean accepts true/1/yes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input type="file" accept=".csv,text/csv" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="import-file" />
          <div className="flex gap-2">
            <button onClick={() => setMode('upsert')} className={`flex-1 px-3 py-2 text-xs font-semibold border rounded ${mode === 'upsert' ? 'bg-[#0B1426] text-white border-[#0B1426]' : 'bg-white border-gray-200'}`}>Upsert (update existing)</button>
            <button onClick={() => setMode('insert_only')} className={`flex-1 px-3 py-2 text-xs font-semibold border rounded ${mode === 'insert_only' ? 'bg-[#0B1426] text-white border-[#0B1426]' : 'bg-white border-gray-200'}`}>Insert only (skip dupes)</button>
          </div>
          <Textarea rows={2} placeholder="Rationale / source description"
            value={rationale} onChange={e => setRationale(e.target.value)} data-testid="import-rationale" />
          {report && (
            <div className="text-xs bg-[#F9FAFB] border border-gray-200 rounded p-3 space-y-1">
              <p><strong>Created:</strong> {report.created} · <strong>Updated:</strong> {report.updated} · <strong>Skipped:</strong> {report.skipped}</p>
              <p><strong>Total rows:</strong> {report.total}</p>
              {report.errors?.length > 0 && (
                <p className="text-[#9B1C1C]"><strong>{report.errors.length} errors</strong> — first: row {report.errors[0].row} · {report.errors[0].error}</p>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0B1426] hover:bg-[#1B2538] text-white" data-testid="import-submit">
            <Upload size={14} className="mr-1" weight="fill" />
            {busy ? 'Importing…' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --------- Main page ----------
export default function FormulaDbPage() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [flag, setFlag] = useState('');
  const [hasNoael, setHasNoael] = useState('');
  const [page, setPage] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState('create');
  const [editorInitial, setEditorInitial] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const LIMIT = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * LIMIT, limit: LIMIT };
      if (q) params.q = q;
      if (flag) params.flag = flag;
      if (hasNoael === 'yes') params.has_noael = true;
      if (hasNoael === 'no') params.has_noael = false;
      const r = await ax.get('/ingredients', { params });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch (e) {
      toast.error('Failed to load ingredients');
    }
    setLoading(false);
  }, [q, flag, hasNoael, page]);

  const loadStats = useCallback(async () => {
    try {
      const r = await ax.get('/stats');
      setStats(r.data);
    } catch (e) { /* tolerated */ }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { load(); }, [load]);

  const openEdit = async (inci) => {
    try {
      const r = await ax.get(`/ingredients/${encodeURIComponent(inci)}`);
      setEditorInitial(r.data.ingredient);
      setEditorMode('edit');
      setEditorOpen(true);
    } catch (e) { toast.error('Failed to load ingredient'); }
  };

  const onAdd = () => { setEditorInitial(null); setEditorMode('create'); setEditorOpen(true); };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (flag) params.set('flag', flag);
    if (hasNoael === 'yes') params.set('has_noael', 'true');
    const url = `${API_URL}/api/admin/formula-db/export?${params}`;
    // Cookie auth carries through window.location request
    window.location.href = url;
  };

  const pageCount = Math.ceil(total / LIMIT);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Database size={22} className="text-[#FF4E6A]" weight="duotone" />
              <h1 className="text-2xl font-bold tracking-tight text-[#111827]">Formula DB Management</h1>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded ml-2">admin only</span>
            </div>
            <p className="text-sm text-[#4B5563]">
              CRUD over the Aᴳ master ingredient DB — add, edit, delete, bulk import/export. All mutations audit-logged.
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/assessor">
              <Button variant="outline" className="h-9 text-xs rounded" data-testid="back-to-assessor">← Assessor Console</Button>
            </Link>
            <Button className="h-9 text-xs rounded bg-[#0B1426] hover:bg-[#1B2538] text-white" onClick={onAdd} data-testid="add-ingredient">
              <Plus size={14} className="mr-1" weight="bold" /> Add ingredient
            </Button>
            <Button className="h-9 text-xs rounded bg-[#FF4E6A] hover:bg-[#E63E5A] text-white" onClick={() => setImportOpen(true)} data-testid="bulk-import-btn">
              <Upload size={14} className="mr-1" weight="bold" /> Bulk import
            </Button>
            <Button variant="outline" className="h-9 text-xs rounded" onClick={exportCsv} data-testid="export-csv">
              <DownloadSimple size={14} className="mr-1" weight="bold" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <StatCard label="Ingredients" value={stats.total.toLocaleString()} color="text-[#FF4E6A]" />
            <StatCard label="With NOAEL" value={`${stats.with_noael.toLocaleString()} (${stats.noael_coverage_pct}%)`} />
            <StatCard label="EU banned" value={stats.eu_banned.toLocaleString()} color="text-[#9B1C1C]" />
            <StatCard label="Prop 65" value={stats.prop_65.toLocaleString()} color="text-[#723B13]" />
            <StatCard label="IFRA restricted" value={stats.ifra_restricted.toLocaleString()} />
            <StatCard label="Genotoxic" value={stats.genotoxic.toLocaleString()} color="text-[#9B1C1C]" />
          </div>
        )}

        {/* Filters */}
        <div className="card-flat p-4 mb-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[240px]">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">Search INCI / CAS</p>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={q} onChange={e => { setQ(e.target.value); setPage(0); }}
                className="pl-7" placeholder="e.g. Glycerin / 56-81-5" data-testid="filter-q" />
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">Flag</p>
            <select value={flag} onChange={e => { setFlag(e.target.value); setPage(0); }}
              className="h-9 text-xs border border-gray-200 rounded px-2 bg-white" data-testid="filter-flag">
              <option value="">All</option>
              {FLAG_OPTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">NOAEL</p>
            <select value={hasNoael} onChange={e => { setHasNoael(e.target.value); setPage(0); }}
              className="h-9 text-xs border border-gray-200 rounded px-2 bg-white" data-testid="filter-noael">
              <option value="">Any</option>
              <option value="yes">With NOAEL</option>
              <option value="no">Without NOAEL</option>
            </select>
          </div>
          {(q || flag || hasNoael) && (
            <Button variant="ghost" className="h-9 text-xs" onClick={() => { setQ(''); setFlag(''); setHasNoael(''); setPage(0); }}>
              <X size={12} className="mr-1" /> Reset
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="card-flat overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#F3F4F6] border-b border-gray-200">
                <tr className="text-left">
                  <th className="px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-[#4B5563]">INCI</th>
                  <th className="px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-[#4B5563]">CAS</th>
                  <th className="px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-[#4B5563]">Function</th>
                  <th className="px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-[#4B5563] text-right">NOAEL</th>
                  <th className="px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-[#4B5563]">Flags</th>
                  <th className="px-3 py-2 font-bold uppercase tracking-wider text-[10px] text-[#4B5563] text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="ingredients-tbody">
                {loading ? (
                  <tr><td colSpan="6" className="px-3 py-8 text-center text-[#4B5563]">Loading…</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan="6" className="px-3 py-8 text-center text-[#4B5563]">No ingredients match the current filters.</td></tr>
                ) : (
                  items.map((i) => (
                    <tr key={i.inci_name} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`row-${i.inci_name}`}>
                      <td className="px-3 py-2 font-semibold text-[#111827]">{i.inci_name}</td>
                      <td className="px-3 py-2 font-mono text-[10px] text-[#4B5563]"><EmptyVal v={i.cas_number} /></td>
                      <td className="px-3 py-2 text-[#4B5563]"><EmptyVal v={i.function_class} /></td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {i.noael !== undefined && i.noael !== null
                          ? <span className="font-semibold text-[#0E9F6E]">{i.noael}</span>
                          : <span className="text-[10px] text-[#9B1C1C] font-bold">DG</span>}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-0.5">
                          {i.eu_annex_ii && <FlagPill label="EU-II" on />}
                          {i.eu_annex_iii && <FlagPill label="EU-III" on />}
                          {i.ifra_restricted && <FlagPill label="IFRA" on />}
                          {i.prop_65 && <FlagPill label="P65" on />}
                          {i.genotoxicity && <FlagPill label="GENO" on />}
                          {i.carcinogenic && <FlagPill label="CARC" on />}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button variant="ghost" className="h-7 text-[10px] px-2"
                          onClick={() => openEdit(i.inci_name)} data-testid={`edit-${i.inci_name}`}>
                          <PencilSimple size={12} className="mr-1" /> Edit
                        </Button>
                        <Button variant="ghost" className="h-7 text-[10px] px-2 text-[#9B1C1C]"
                          onClick={() => setDeleteTarget(i)} data-testid={`delete-${i.inci_name}`}>
                          <Trash size={12} />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 text-xs text-[#4B5563]">
              <span>Showing {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} of {total.toLocaleString()}</span>
              <div className="flex gap-1">
                <Button variant="outline" className="h-7 text-[10px] px-2" disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
                <span className="px-2 py-1 tabular-nums">Page {page + 1} / {pageCount}</span>
                <Button variant="outline" className="h-7 text-[10px] px-2" disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>Next ›</Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <IngredientEditor open={editorOpen} onClose={() => setEditorOpen(false)}
        initial={editorInitial} mode={editorMode}
        onSaved={() => { load(); loadStats(); }} />
      {deleteTarget && (
        <DeleteConfirm open onClose={() => setDeleteTarget(null)} ingredient={deleteTarget}
          onDone={() => { load(); loadStats(); }} />
      )}
      <BulkImportDialog open={importOpen} onClose={() => setImportOpen(false)}
        onDone={() => { load(); loadStats(); }} />
    </div>
  );
}
